import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Search, X, Type, Hash, ToggleLeft, Users, List } from 'lucide-react';
import axiosInstance from '../../../shared/services/api/axiosInstance';
import { getTransactionProperties, getUserPropertiesForTransaction, getDefaultOperator } from '../config/transactionFilterProperties';
import Icon from '../../../shared/components/Icon';

// Basic event + user predefined properties copied from EventPropertyFilterModule for global use
const getPredefinedEventProperties = () => [
  { name: 'distinct_id', label: 'Distinct ID', type: 'string', source: 'event' },
  { name: 'user_identity', label: 'User Identity', type: 'boolean', source: 'event', special: 'is_identified' },
  { name: 'country', label: 'Country', type: 'string', source: 'event' },
  { name: 'city', label: 'City', type: 'string', source: 'event' },
  { name: 'region', label: 'Region', type: 'string', source: 'event' },
  { name: 'timezone', label: 'Timezone', type: 'string', source: 'event' },
  { name: 'browser_name', label: 'Browser Name', type: 'string', source: 'event' },
  { name: 'browser_version', label: 'Browser Version', type: 'string', source: 'event' },
  { name: 'os', label: 'Operating System', type: 'string', source: 'event' },
  { name: 'os_version', label: 'OS Version', type: 'string', source: 'event' },
  { name: 'device_type', label: 'Device Type', type: 'string', source: 'event' },
  { name: 'language', label: 'Language', type: 'string', source: 'event' },
  { name: 'current_url', label: 'Current URL', type: 'string', source: 'event' },
  { name: 'page_title', label: 'Page Title', type: 'string', source: 'event' },
  { name: 'uri', label: 'URI', type: 'string', source: 'event' },
  { name: 'initial_referrer', label: 'Initial Referrer', type: 'string', source: 'event' },
  { name: 'referrer', label: 'Referrer', type: 'string', source: 'event' },
  { name: 'utm_source', label: 'UTM Source', type: 'string', source: 'event' },
  { name: 'utm_medium', label: 'UTM Medium', type: 'string', source: 'event' },
  { name: 'utm_campaign', label: 'UTM Campaign', type: 'string', source: 'event' },
  { name: 'utm_term', label: 'UTM Term', type: 'string', source: 'event' },
  { name: 'utm_content', label: 'UTM Content', type: 'string', source: 'event' },
  { name: 'utm_id', label: 'UTM ID', type: 'string', source: 'event' },
  { name: 'element_name', label: 'Element Name', type: 'string', source: 'event' },
  { name: 'element_id', label: 'Element ID', type: 'string', source: 'event' },
  { name: 'element_class', label: 'Element Class', type: 'string', source: 'event' },
  { name: 'element_tag_name', label: 'Element Tag Name', type: 'string', source: 'event' },
  { name: 'element_type', label: 'Element Type', type: 'string', source: 'event' },
  { name: 'element_text', label: 'Element Text', type: 'string', source: 'event' },
  { name: 'viewport_height', label: 'Viewport Height', type: 'number', source: 'event' },
  { name: 'viewport_width', label: 'Viewport Width', type: 'number', source: 'event' },
  { name: 'screen_height', label: 'Screen Height', type: 'number', source: 'event' },
  { name: 'screen_width', label: 'Screen Width', type: 'number', source: 'event' },
  { name: 'dpi', label: 'DPI', type: 'number', source: 'event' },
  { name: 'is_web3_user', label: 'Is Web3 User', type: 'boolean', source: 'event' },
  { name: 'wallet_address', label: 'Wallet Address', type: 'boolean', source: 'event', special: 'exists' },
];

// Shared user properties (same as EventPropertyFilterModule / transactionFilterProperties)
const getPredefinedUserProperties = () => getUserPropertiesForTransaction();

const ObjectIcon = ({ color = "currentColor", size = 18 }) => (
  <span style={{ fontSize: size, color }} className="font-mono">{"{}"}</span>
);

function getDataTypeIcon(type) {
  switch (type) {
    case 'string': return Type;
    case 'number': return Hash;
    case 'datetime': return ({ color = "currentColor", size = 18 }) => <Icon name="calendar" size={size} color={color} />;
    case 'boolean': return ToggleLeft;
    case 'list': return List;
    case 'object': return ObjectIcon;
    case 'cohort': return Users;
    default: return Type;
  }
}

/**
 * Global Property Filter Module
 *
 * Single popup used for GLOBAL filters only.
 * Shows All / Events / Users / Transactions / First time in one place.
 * Does NOT replace event-specific or transaction-specific popups.
 */
const GlobalPropertyFilterModule = ({
  isOpen,
  onClose,
  triggerRef,
  teamId,
  existingFilter = null,
  onGlobalFilterAdd,        // (filter) => void
  firstTimeOnly = false,
  onFirstTimeChange = null, // (enabled: boolean) => void
  isBreakdownPicker = false, // When true: Cohort is a dimension like Country; add breakdown by all cohorts without selecting
  allowTransactionFilters = true,
}) => {
  const [selectedNavItem, setSelectedNavItem] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const popupRef = useRef(null);

  const [eventProperties] = useState(() => getPredefinedEventProperties());
  const [transactionProperties] = useState(() => getTransactionProperties());
  const [userProperties, setUserProperties] = useState(() => getPredefinedUserProperties());
  const [loadingUserCustom, setLoadingUserCustom] = useState(false);
  const [hasLoadedUserCustom, setHasLoadedUserCustom] = useState(false);
  const [cohorts, setCohorts] = useState([]);
  const [loadingCohorts, setLoadingCohorts] = useState(false);
  const [expandedObjectProperty, setExpandedObjectProperty] = useState(null);

  // Reset custom-load flag when team changes
  useEffect(() => {
    setHasLoadedUserCustom(false);
  }, [teamId]);

  useEffect(() => { if (!isOpen) setExpandedObjectProperty(null); }, [isOpen]);

  // When editing a cohort filter, open Cohorts tab
  useEffect(() => {
    if (isOpen && existingFilter && (existingFilter.category === 'cohort' || existingFilter.propertySource === 'cohort')) {
      setSelectedNavItem('cohorts');
    }
  }, [isOpen, existingFilter]);

  // Fetch user custom properties once per team for global mode
  useEffect(() => {
    const fetchUserCustomProps = async () => {
      if (!isOpen || !teamId || hasLoadedUserCustom) return;
      setLoadingUserCustom(true);
      try {
        let numericTeamId = null;
        if (typeof teamId === 'object' && teamId !== null) {
          numericTeamId = parseInt(teamId._id || teamId.id, 10);
        } else if (typeof teamId === 'string') {
          try {
            const teamDataStr = localStorage.getItem('selectedTeamData');
            if (teamDataStr) {
              const teamData = JSON.parse(teamDataStr);
              numericTeamId = parseInt(teamData._id || teamData.id, 10);
            } else {
              numericTeamId = parseInt(teamId, 10);
            }
          } catch {
            numericTeamId = parseInt(teamId, 10);
          }
        } else {
          numericTeamId = parseInt(teamId, 10);
        }
        if (isNaN(numericTeamId) || !numericTeamId) {
          setLoadingUserCustom(false);
          return;
        }

        const res = await axiosInstance.get('/events/user-custom-properties', {
          params: { team_id: numericTeamId, limit: 5000 },
        });

        if (res.data?.success && Array.isArray(res.data.custom_properties)) {
          const custom = res.data.custom_properties.map((prop) => {
            let propertyType = prop.type || 'string';
            let listItemType = null;
            if (propertyType.startsWith('list<') && propertyType.endsWith('>')) {
              listItemType = propertyType.slice(5, -1);
              propertyType = 'list';
            }
            return {
              name: `user.${prop.name}`,
              label: prop.name,
              type: propertyType,
              listItemType,
              source: 'user',
              isCustom: true,
              sampleValues: prop.sampleValues || [],
              nestedProperties: prop.nestedProperties || undefined,
            };
          });

          setUserProperties((prev) => {
            const merged = [...prev, ...custom];
            const byName = new Map();
            merged.forEach((prop) => {
              if (!byName.has(prop.name)) {
                byName.set(prop.name, prop);
              }
            });
            return Array.from(byName.values());
          });
          setHasLoadedUserCustom(true);
        }
      } catch (e) {
        console.error('Error fetching user custom properties for global filter:', e);
      } finally {
        setLoadingUserCustom(false);
      }
    };

    fetchUserCustomProps();
  }, [isOpen, teamId, hasLoadedUserCustom]);

  const parseTeamId = () => {
    if (!teamId) return null;
    if (typeof teamId === 'object' && teamId !== null) {
      return parseInt(teamId._id || teamId.id, 10);
    }
    if (typeof teamId === 'string') {
      try {
        const teamDataStr = localStorage.getItem('selectedTeamData');
        if (teamDataStr) {
          const teamData = JSON.parse(teamDataStr);
          return parseInt(teamData._id || teamData.id, 10);
        }
        return parseInt(teamId, 10);
      } catch {
        return parseInt(teamId, 10);
      }
    }
    return parseInt(teamId, 10);
  };

  useEffect(() => {
    const fetchCohorts = async () => {
      if (!isOpen || selectedNavItem !== 'cohorts') return;
      const numericTeamId = parseTeamId();
      if (isNaN(numericTeamId) || !numericTeamId) return;
      setLoadingCohorts(true);
      try {
        const res = await axiosInstance.get('/cohorts', {
          params: { team_id: numericTeamId, include_user_count: false },
        });
        if (res.data?.success && Array.isArray(res.data.cohorts)) {
          setCohorts(res.data.cohorts);
        }
      } catch (e) {
        console.error('Error fetching cohorts for global filter:', e);
      } finally {
        setLoadingCohorts(false);
      }
    };
    fetchCohorts();
  }, [isOpen, selectedNavItem, teamId]);

  // Position popup near trigger with viewport bounds checking (mirrors TransactionPropertyFilterModule).
  // When no triggerRef is available (rare editing edge cases), fall back to centering the popup
  // so it never appears stuck at the top-left of the viewport.
  useEffect(() => {
    if (!isOpen) return;

    const scrollY = window.scrollY || window.pageYOffset;
    const scrollX = window.scrollX || window.pageXOffset;
    const popupWidth = 480;
    const popupHeight = 380;
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const margin = 16;

    let top;
    let left;

    if (triggerRef?.current) {
      const triggerRect = triggerRef.current.getBoundingClientRect();

      top = triggerRect.bottom + scrollY + 8;
      left = triggerRect.left + scrollX;

      if (left + popupWidth > viewportWidth - margin) {
        left = triggerRect.right + scrollX - popupWidth;
        if (left < margin) left = viewportWidth - popupWidth - margin;
      }
      if (left < margin) left = margin;

      if (top + popupHeight > viewportHeight + scrollY - margin) {
        top = triggerRect.top + scrollY - popupHeight - 8;
        if (top < scrollY + margin) top = scrollY + margin;
      }
    } else {
      // Fallback: center the popup
      left = scrollX + (viewportWidth - popupWidth) / 2;
      top = scrollY + (viewportHeight - popupHeight) / 2;
    }

    if (top < scrollY + margin) top = scrollY + margin;

    setPosition({ top, left });
  }, [isOpen, triggerRef]);

  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        isOpen &&
        popupRef.current &&
        !popupRef.current.contains(e.target) &&
        triggerRef?.current &&
        !triggerRef.current.contains(e.target)
      ) {
        onClose();
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen, onClose, triggerRef]);

  // Close on Escape
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) onClose();
    };
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const sourceTransactionProperties = allowTransactionFilters ? transactionProperties : [];
  const allProps = [
    ...eventProperties,
    ...sourceTransactionProperties,
    ...userProperties,
  ];

  const propsByNav = {
    all: isBreakdownPicker ? [...allProps, { name: 'cohort', label: 'Cohort', type: 'cohort', source: 'cohort' }] : allProps,
    events: eventProperties,
    users: isBreakdownPicker ? [...userProperties, { name: 'cohort', label: 'Cohort', type: 'cohort', source: 'cohort' }] : userProperties,
    transactions: sourceTransactionProperties,
  };

  const propsList = selectedNavItem === 'first_time'
    ? allProps
    : (propsByNav[selectedNavItem] || allProps);

  const filteredProps = propsList.filter((p) =>
    (p.label || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (p.name || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleAddNestedPropertyFilter = (parentProperty, nestedProp) => {
    if (typeof onGlobalFilterAdd !== 'function') return;
    const nestedType = nestedProp.type || 'string';
    let listItemType = null;
    if (nestedType.startsWith('list<') && nestedType.endsWith('>')) {
      listItemType = nestedType.slice(5, -1);
    }
    const fullPath = `${parentProperty.name}.${nestedProp.name}`;
    const effectiveType = nestedType.startsWith('list') ? 'list' : nestedType;
    onGlobalFilterAdd({
      id: existingFilter?.id || `global-filter-${Date.now()}-${Math.random().toString(36).slice(2)}`,
      property: fullPath,
      propertyLabel: `${parentProperty.label}.${nestedProp.name}`,
      propertyType: effectiveType,
      propertySource: parentProperty.source,
      operator: getDefaultOperator(effectiveType),
      value: null,
      value2: null,
      sampleValues: nestedProp.sampleValues || [],
      listItemType: listItemType || undefined,
      listMode: effectiveType === 'list' ? 'any_in_list' : undefined,
    });
    setExpandedObjectProperty(null);
    onClose();
  };

  const handleAddPropertyFilter = (property) => {
    if (property.type === 'object' && property.nestedProperties?.length > 0) {
      setExpandedObjectProperty(property);
      return;
    }
    if (typeof onGlobalFilterAdd !== 'function') {
      onClose();
      return;
    }

    const baseId = existingFilter?.id || `global-filter-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    const didChangeProperty = existingFilter ? existingFilter.property !== property.name : true;
    const operator = existingFilter && property.type === existingFilter.propertyType
      ? existingFilter.operator
      : getDefaultOperator(property.type);

    const newFilter = {
      id: baseId,
      property: property.name,
      propertyLabel: property.label,
      propertyType: property.type,
      propertySource: property.source, // 'event' | 'transaction' | 'user'
      ...(property.source === 'transaction' && { category: 'transaction_properties' }),
      operator,
      // Keep existing values only when the property itself did not change AND the type still matches.
      // This prevents "N selected" from leaking when switching from one property to another (e.g. Decoded address -> Country).
      value: existingFilter && !didChangeProperty && property.type === existingFilter.propertyType ? existingFilter.value : null,
      value2: existingFilter && !didChangeProperty && property.type === existingFilter.propertyType ? existingFilter.value2 : null,
      ...(property.sampleValues?.length && { sampleValues: property.sampleValues }),
      ...(property.listItemType != null && { listItemType: property.listItemType }),
      ...(property.nestedProperties?.length && { nestedProperties: property.nestedProperties }),
      ...(property.type === 'list' && { listMode: existingFilter?.listMode || 'any_in_list' }),
    };

    onGlobalFilterAdd(newFilter);
    onClose();
  };

  const handleCohortClick = (cohort) => {
    if (typeof onGlobalFilterAdd !== 'function') return;
    onGlobalFilterAdd({
      id: `global-cohort-${Date.now()}-${Math.random().toString(36).slice(2)}`,
      category: 'cohort',
      propertySource: 'cohort',
      field: 'cohort',
      value: [cohort.id],
      propertyLabel: cohort.name,
      connector: 'AND',
    });
    onClose();
  };

  const handleFirstTimeClick = () => {
    // For global filters, we delegate to parent (QueryBuilderPanel) to build the first_time filter
    if (typeof onFirstTimeChange === 'function') {
      onFirstTimeChange(!firstTimeOnly);
      onClose();
      return;
    }
    if (typeof onGlobalFilterAdd === 'function') {
      onGlobalFilterAdd({
        id: `global-first-time-${Date.now()}`,
        category: 'first_time',
      });
      onClose();
    }
  };

  return createPortal(
    <div
      ref={popupRef}
      className="fixed z-[9999] bg-white border border-gray-200 rounded-lg shadow-xl flex flex-col overflow-hidden"
      style={{
        top: `${position.top}px`,
        left: `${position.left}px`,
        width: '480px',
        maxHeight: '380px',
        fontFamily: 'Raleway, sans-serif',
      }}
    >
      {/* Search */}
      <div className="px-3 py-2 border-b border-gray-200 flex-shrink-0">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#475569]" />
          <input
            type="text"
            placeholder="Search properties..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-9 pr-8 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1d0c46]"
          />
          {searchTerm && (
            <button
              type="button"
              onClick={() => setSearchTerm('')}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-[#475569] hover:text-[#475569]"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>

      <div className="flex flex-1 min-h-0 overflow-hidden">
        {/* Left nav */}
        <div className="w-32 border-r border-gray-200 bg-gray-50 flex-shrink-0 py-2">
          {[
            { id: 'all', label: 'All' },
            { id: 'events', label: 'Events' },
            { id: 'users', label: 'Users' },
            ...(allowTransactionFilters ? [{ id: 'transactions', label: 'Transactions' }] : []),
            { id: 'cohorts', label: 'Cohorts' },
            { id: 'first_time', label: 'First time only' },
          ].map((item) => (
            <button
              key={item.id}
              type="button"
              onClick={() => {
                if (item.id === 'first_time') {
                  handleFirstTimeClick();
                  return;
                }
                if (item.id === 'cohorts' && isBreakdownPicker && typeof onGlobalFilterAdd === 'function') {
                  onGlobalFilterAdd({
                    property: 'cohort',
                    propertyLabel: 'Cohort',
                    propertySource: 'cohort',
                    category: 'cohort',
                  });
                  onClose();
                  return;
                }
                setSelectedNavItem(item.id);
              }}
              className={`w-full text-left px-3 py-1.5 text-sm transition-colors ${
                selectedNavItem === item.id
                  ? 'bg-white border-l-2 border-[#4b57aa] text-[#4b57aa] font-semibold'
                  : 'text-[#475569] hover:bg-gray-100'
              }`}
            >
              {item.label}
            </button>
          ))}
          {loadingUserCustom && (
            <div className="px-3 pt-1 text-[11px] text-[#64748b]">
              Loading user properties…
            </div>
          )}
        </div>

        {/* Properties list / Cohorts / Nested (object) */}
        <div className="flex-1 min-h-0 overflow-y-auto p-2 flex flex-col">
          {expandedObjectProperty ? (
            <>
              <button
                type="button"
                onClick={() => setExpandedObjectProperty(null)}
                className="flex items-center gap-1 text-xs text-[#4b57aa] hover:underline mb-2"
              >
                ← Back to properties
              </button>
              <div className="text-xs font-medium text-[#475569] mb-2">
                Select nested property in "{expandedObjectProperty.label}"
              </div>
              <div className="space-y-1">
                {expandedObjectProperty.nestedProperties?.map((np) => {
                  const NestedIcon = getDataTypeIcon(np.type?.startsWith('list') ? 'list' : np.type || 'string');
                  return (
                    <button
                      key={np.name}
                      type="button"
                      onClick={() => handleAddNestedPropertyFilter(expandedObjectProperty, np)}
                      className="w-full text-left px-3 py-2 text-sm rounded hover:bg-gray-50 flex items-center gap-2"
                    >
                      <NestedIcon className="h-4 w-4 text-[#475569] flex-shrink-0" />
                      <span className="flex-1 text-[#0f172a]">{np.name}</span>
                      <span className="text-xs text-[#64748b]">{np.type || 'string'}</span>
                    </button>
                  );
                })}
              </div>
            </>
          ) : selectedNavItem === 'cohorts' ? (
            <>
              {isBreakdownPicker ? (
                <div className="p-2">
                  <div className="text-xs text-[#475569] mb-2">Break down by Cohort segments data by each cohort, like Country or Device.</div>
                  <button
                    type="button"
                    onClick={() => {
                      if (typeof onGlobalFilterAdd === 'function') {
                        onGlobalFilterAdd({
                          property: 'cohort',
                          propertyLabel: 'Cohort',
                          propertySource: 'cohort',
                          category: 'cohort',
                        });
                        onClose();
                      }
                    }}
                    className="w-full text-left px-3 py-2 text-sm rounded hover:bg-gray-50 flex items-center gap-2"
                  >
                    <Users className="h-4 w-4 text-[#475569] flex-shrink-0" />
                    <span className="font-medium">Break down by Cohort</span>
                  </button>
                </div>
              ) : loadingCohorts ? (
                <div className="p-4 text-sm text-[#475569] text-center">Loading cohorts…</div>
              ) : cohorts.length === 0 ? (
                <div className="p-4 text-sm text-[#475569] text-center">No cohorts. Create cohorts in the Cohorts page.</div>
              ) : (
                <div className="flex-1 overflow-y-auto space-y-0.5">
                  {cohorts
                    .filter((c) => !searchTerm || (c.name || '').toLowerCase().includes(searchTerm.toLowerCase()))
                    .map((cohort) => (
                    <button
                      key={cohort.id}
                      type="button"
                      onClick={() => handleCohortClick(cohort)}
                      className="w-full text-left px-3 py-2 text-sm rounded transition-colors flex items-center gap-2 hover:bg-gray-50"
                    >
                      <Users className="h-4 w-4 text-[#475569] flex-shrink-0" />
                      <span className="flex-1">{cohort.name}</span>
                    </button>
                  ))}
                </div>
              )}
            </>
          ) : filteredProps.length === 0 ? (
            <div className="p-4 text-sm text-[#475569] text-center">
              {searchTerm ? 'No properties match.' : 'No properties.'}
            </div>
          ) : (
            <div className="space-y-0.5">
              {filteredProps.map((prop, idx) => {
                const Icon = getDataTypeIcon(prop.type);
                const isSelected =
                  existingFilter &&
                  existingFilter.property === prop.name &&
                  existingFilter.propertySource === prop.source;
                const sourceTag =
                  prop.source === 'user'
                    ? 'U'
                    : prop.source === 'transaction'
                    ? 'T'
                    : 'E';
                return (
                  <button
                    key={`${prop.source}-${prop.name}-${idx}`}
                    type="button"
                    onClick={() => handleAddPropertyFilter(prop)}
                    className={`w-full text-left px-3 py-2 text-sm rounded transition-colors flex items-center gap-2 ${
                      isSelected ? 'bg-gray-200 border border-gray-300' : 'hover:bg-gray-50'
                    }`}
                  >
                    <Icon className="h-4 w-4 text-[#475569] flex-shrink-0" />
                    <span className="flex-1">{prop.label}</span>
                    <span
                      className="text-[10px] font-semibold uppercase w-5 h-5 inline-flex items-center justify-center rounded bg-gray-200 text-[#475569]"
                      title={
                        prop.source === 'user'
                          ? 'User'
                          : prop.source === 'transaction'
                          ? 'Transaction'
                          : 'Event'
                      }
                    >
                      {sourceTag}
                    </span>
                    {isSelected && (
                      <span className="text-xs text-[#475569]">(current)</span>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default GlobalPropertyFilterModule;

